import { defineStore } from 'pinia'
import { HOME_TAB_ID, HOME_TAB_PATH, HOME_TAB_TITLE, HOME_TAB_MSGKEY } from '~/constants'
import { ElMessage } from 'element-plus'
import { MAX_TAB_COUNT } from '~/constants'

// ─────────────────────────────────────────────
// 상수
// 탭 개수 제한은 이 변수 하나로만 관리한다.
// 값을 바꾸려면 constants/index.ts 의 MAX_TAB_COUNT 를 수정하면 된다.
// ─────────────────────────────────────────────
const MAX_TABS = MAX_TAB_COUNT

// ─────────────────────────────────────────────
// 타입
// ─────────────────────────────────────────────
export interface Tab {
  /** 고유 식별자 — 라우트 경로를 그대로 사용한다 (예: '/base/system/auth/users') */
  id: string
  /** 탭에 표시될 레이블 (msgKey 가 없을 때 폴백) */
  title: string
  /** 실제 라우트 경로 (id 와 동일하게 유지) */
  path: string
  /**
   * false 이면 닫기 버튼을 표시하지 않는다.
   * 대시보드처럼 항상 열려 있어야 하는 탭에 사용한다.
   */
  closable: boolean
  /**
   * 다국어 키 — 존재하면 $t(msgKey) 로 렌더링, 없으면 title 폴백.
   * menus.msg_key 에서 가져온다.
   */
  msgKey?: string
}

// ─────────────────────────────────────────────
// 홈(대시보드) 탭 — 항상 첫 번째 탭으로 고정된다
// ─────────────────────────────────────────────
const HOME_TAB: Readonly<Tab> = {
  id: HOME_TAB_ID,
  title: HOME_TAB_TITLE,
  path: HOME_TAB_PATH,
  closable: false,
  msgKey: HOME_TAB_MSGKEY,
}

function makeHomeTab(): Tab {
  return { ...HOME_TAB }
}

// ─────────────────────────────────────────────
// Store
// ─────────────────────────────────────────────
export const useTabStore = defineStore('tab', {
  state: () => ({
    tabs: [makeHomeTab()] as Tab[],
    activeTabId: HOME_TAB.id as string,
  }),

  getters: {
    /** 현재 활성화된 탭 객체. 탭 목록이 비어 있으면 null. */
    activeTab: (state): Tab | null =>
      state.tabs.find(t => t.id === state.activeTabId) ?? null,
  },

  actions: {
    // ── 탭 추가 ──────────────────────────────

    /**
     * 탭을 추가한다.
     *
     * - 이미 열려 있는 탭이면 해당 탭을 활성화하고 `true` 반환
     * - `MAX_TABS` 초과 시 ElMessage 경고를 표시하고 `false` 반환
     * - 정상 추가 시 `true` 반환
     *
     * 반환값이 `false` 이면 라우터 이동을 취소해야 한다.
     */
    addTab(tab: Tab): boolean {
      const existing = this.tabs.find(t => t.id === tab.id)
      if (existing) {
        this.activeTabId = tab.id
        return true
      }

      if (this.tabs.length >= MAX_TABS) {
        ElMessage.warning(`탭은 최대 ${MAX_TABS}개까지 열 수 있습니다.`)
        return false
      }

      this.tabs.push(tab)
      this.activeTabId = tab.id
      return true
    },

    // ── 탭 삭제 ──────────────────────────────

    /**
     * 지정한 탭을 제거한다.
     *
     * - `closable: false` 인 탭은 제거하지 않는다.
     * - 활성 탭이 제거되면 인접 탭(다음 → 이전 → 첫 번째)으로 포커스를 이동한다.
     *
     * @returns 라우터가 이동해야 할 `path` — 이동 불필요 시 `null`
     */
    removeTab(tabId: string): string | null {
      const idx = this.tabs.findIndex(t => t.id === tabId)
      if (idx === -1) return null

      const tab = this.tabs[idx]
      if (!tab.closable) return null

      this.tabs.splice(idx, 1)

      // 닫힌 탭이 활성 탭이 아니었으면 라우터 이동 불필요
      if (this.activeTabId !== tabId) return null

      // 인접 탭으로 활성화 이동: 다음 > 이전 > 첫 번째
      const next = this.tabs[idx] ?? this.tabs[idx - 1] ?? this.tabs[0]
      if (next) {
        this.activeTabId = next.id
        return next.path
      }

      return null
    },

    /**
     * 지정한 탭(tabId)을 제외한 닫기 가능한 모든 탭을 제거한다.
     *
     * @returns 남아 있는 탭(tabId)의 `path`
     */
    removeOtherTabs(tabId: string): string {
      this.tabs = this.tabs.filter(t => !t.closable || t.id === tabId)
      this.activeTabId = tabId
      return this.tabs.find(t => t.id === tabId)?.path ?? HOME_TAB.path
    },

    /**
     * 닫기 가능한 모든 탭을 제거하고 홈(대시보드) 탭으로 이동한다.
     *
     * @returns 이동해야 할 `path`
     */
    removeAllTabs(): string {
      this.tabs = this.tabs.filter(t => !t.closable)

      // closable:false 탭이 하나도 없는 경우(비정상 상태) 홈 탭을 복구
      if (!this.tabs.length) this.tabs.push(makeHomeTab())

      const target = this.tabs[0]
      this.activeTabId = target.id
      return target.path
    },

    /**
     * 닫기 가능한 모든 탭을 제거하고 홈(대시보드) 탭으로 이동한다.
     * 대시보드(/admin) 탭은 closable:false 이므로 항상 고정(Leftover)된다.
     *
     * @returns 이동해야 할 `path` (항상 HOME_TAB.path)
     */
    closeAllTabs(): string {
      return this.removeAllTabs()
    },

    // ── 활성 탭 변경 ─────────────────────────

    /**
     * 활성 탭 ID를 변경한다.
     * 라우터 이동은 호출하는 쪽에서 별도로 처리해야 한다.
     */
    setActiveTab(tabId: string) {
      this.activeTabId = tabId
    },

    // ── 탭 순서 변경 ─────────────────────────

    /**
     * fromId 탭을 toId 탭의 위치로 이동한다.
     * closable:false(대시보드 등) 탭은 이동 대상에서 제외한다.
     */
    reorderTabs(fromId: string, toId: string, insertAfter = false) {
      if (fromId === toId) return
      const toTab = this.tabs.find(t => t.id === toId)
      if (!toTab?.closable) return
      const fromIdx = this.tabs.findIndex(t => t.id === fromId)
      if (fromIdx === -1) return
      const [tab] = this.tabs.splice(fromIdx, 1)
      const newToIdx = this.tabs.findIndex(t => t.id === toId)
      this.tabs.splice(insertAfter ? newToIdx + 1 : newToIdx, 0, tab)
    },

    // ── 초기화 ───────────────────────────────

    /** 스토어를 초기 상태(대시보드 탭만 존재)로 되돌린다. */
    reset() {
      this.tabs = [makeHomeTab()]
      this.activeTabId = HOME_TAB.id
    },
  },
})
